/**
 * The Terms, Privacy and Cookie texts live in ONE place: the static pages
 * client/public/{terms,privacy,cookies}.html. Vercel serves them at /terms,
 * /privacy and /cookies to crawlers that run no JavaScript (TikTok and Meta
 * review), and server/src/static mirrors terms and privacy for api.buzzbitx.com.
 *
 * In-app navigation must keep the clean URL (no `.html`) and the shared
 * landing Navbar/Footer. This route fetches the static file, extracts the
 * article body, and renders it inside that chrome — never location.replace
 * to /privacy.html.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { t, type MessageKey } from '@/i18n';
import { Navbar } from '../Landing/components/Navbar';
import { Footer } from '../Landing/components/Footer';

export type StaticLegalDoc = 'terms' | 'privacy' | 'cookies';

const TITLE: Record<StaticLegalDoc, MessageKey> = {
  terms: 'legal.termsLink',
  privacy: 'legal.privacyLink',
  cookies: 'legal.cookiesLink',
};

/** Static asset path — fetched only; the address bar stays on /terms etc. */
function staticLegalSrc(doc: StaticLegalDoc): string {
  return `/${doc}.html`;
}

function extractArticle(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.querySelector('.container')?.innerHTML ?? '';
}

export default function StaticLegalPage({ doc }: { doc: StaticLegalDoc }): JSX.Element {
  const { hash } = useLocation();
  const navigate = useNavigate();
  const articleRef = useRef<HTMLElement>(null);
  const [article, setArticle] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const src = staticLegalSrc(doc);

  useEffect(() => {
    let cancelled = false;
    setArticle(null);
    setFailed(false);
    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((html) => {
        if (cancelled) return;
        const body = extractArticle(html);
        if (!body) throw new Error('empty article');
        setArticle(body);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => { cancelled = true; };
  }, [src]);

  useEffect(() => {
    if (!article || !hash) return undefined;
    const id = decodeURIComponent(hash.slice(1));
    const scroll = (): boolean => {
      const el = document.getElementById(id);
      if (!el) return false;
      el.scrollIntoView({ block: 'start' });
      return true;
    };
    if (scroll()) return undefined;
    let attempts = 0;
    const tick = (): void => {
      attempts += 1;
      if (scroll() || attempts >= 10) return;
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
    return undefined;
  }, [article, hash]);

  // Keep in-app legal hops inside the SPA so the shared Navbar/Footer stay mounted
  // (a full load of /privacy would hit Vercel's static rewrite instead).
  useEffect(() => {
    const root = articleRef.current;
    if (!root || !article) return undefined;
    const onClick = (event: MouseEvent): void => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const anchor = (event.target as Element | null)?.closest?.('a');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('//')) return;
      event.preventDefault();
      navigate(href);
    };
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [article, navigate]);

  return (
    <div className="min-h-screen bg-white text-gray-600">
      <Navbar />
      <main className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
        {article && (
          <article
            ref={articleRef}
            className="legal-static-doc space-y-2 [&_a]:text-teal-600 [&_a]:underline hover:[&_a]:text-teal-700 [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:text-[0.92em] [&_h1]:mb-2 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-gray-900 md:[&_h1]:text-4xl [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:border-b [&_h2]:border-gray-200 [&_h2]:pb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-gray-900 [&_h2]:scroll-mt-28 [&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-gray-900 [&_li]:mb-1.5 [&_p]:mb-3 [&_p]:leading-relaxed [&_strong]:font-semibold [&_strong]:text-gray-900 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_td]:border [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-2 [&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_ul]:mb-4 [&_ul]:list-disc [&_ul]:pl-5 [&_.contact-card]:my-3 [&_.contact-card]:rounded-lg [&_.contact-card]:border [&_.contact-card]:border-gray-200 [&_.contact-card]:bg-gray-50 [&_.contact-card]:p-5 [&_.dates]:mb-8 [&_.dates]:text-sm [&_.dates]:text-gray-500 [&_.subtitle]:mb-2 [&_.subtitle]:text-lg [&_.subtitle]:text-gray-500 [&_.table-wrap]:my-3 [&_.table-wrap]:overflow-x-auto"
            // Own static legal HTML (client/public) — single source of truth with Vercel/API mirrors.
            dangerouslySetInnerHTML={{ __html: article }}
          />
        )}
        {!article && failed && (
          <p className="text-center text-sm">
            <a href={src} className="text-teal-600 underline">
              {t('legal.openDocument', { title: t(TITLE[doc]) })}
            </a>
          </p>
        )}
        {!article && !failed && (
          <p className="text-center text-sm text-gray-400">{t('legal.loadingDocument')}</p>
        )}
      </main>
      <Footer />
    </div>
  );
}
